import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';

const MAX_LOGO_BYTES = 200 * 1024; // Firestore documents are capped at 1 MB
const MAX_LOGO_PX = 256;

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/** Downscale an image file to MAX_LOGO_PX (longest edge) and return a data URL. */
const downscaleImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read the selected file'));
  reader.onload = (e) => {
    const img = new Image();
    img.onerror = () => reject(new Error('The selected file is not a supported image'));
    img.onload = () => {
      const scale = Math.min(1, MAX_LOGO_PX / Math.max(img.width, img.height));
      const width = Math.max(1, Math.round(img.width * scale));
      const height = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      // Keep transparency for PNG/SVG sources; everything else becomes JPEG
      const keepAlpha = file.type === 'image/png' || file.type === 'image/svg+xml' || file.type === 'image/webp';
      resolve(canvas.toDataURL(keepAlpha ? 'image/png' : 'image/jpeg', 0.85));
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});

const CompanySettings = () => {
  const { companyConfig, updateCompanyConfiguration, loading } = useSettings();
  const { refreshUser } = useAuth();
  const { canManageCompany } = usePermission();

  const [localConfig, setLocalConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (companyConfig) {
      setLocalConfig({
        profile: JSON.parse(JSON.stringify(companyConfig.profile || {})),
        branding: JSON.parse(JSON.stringify(companyConfig.branding || {})),
        ui: JSON.parse(JSON.stringify(companyConfig.ui || {}))
      });
    }
  }, [companyConfig]);

  const handleSettingChange = (category, field, value) => {
    setLocalConfig(prev => ({ ...prev, [category]: { ...prev[category], [field]: value } }));
  };

  const handleContactChange = (field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      profile: { ...prev.profile, contactInfo: { ...(prev.profile?.contactInfo || {}), [field]: value } }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setWarning(null);

      // Only the subtrees this screen edits are sent; the backend deep-merges them.
      await updateCompanyConfiguration({
        profile: localConfig.profile,
        branding: localConfig.branding,
        ui: localConfig.ui
      });

      // Keep the tenant company record's name in sync with the display name
      const newName = (localConfig.profile?.displayName || '').trim();
      const oldName = (companyConfig?.profile?.displayName || '').trim();
      if (newName && newName !== oldName) {
        try {
          await apiService.companies.updateProfile({ name: newName });
          await refreshUser().catch(() => {});
        } catch (err) {
          setWarning(`Settings saved, but the company name could not be updated: ${serverMessage(err, 'unknown error')}`);
        }
      }

      setSuccess('Company settings updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(serverMessage(err, 'Failed to save company settings. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setError(null);

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (PNG, JPEG, SVG or WebP).');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError(`Logo must be 200 KB or smaller (selected file is ${Math.round(file.size / 1024)} KB).`);
      return;
    }
    try {
      const dataUrl = await downscaleImage(file);
      handleSettingChange('branding', 'logo', dataUrl);
    } catch (err) {
      setError(err.message || 'Could not process the selected image');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!companyConfig || !localConfig) {
    return (
      <Alert severity="warning">
        Company settings are unavailable. The company configuration could not be loaded; this usually means
        your role does not include company settings access, or the settings service is unreachable.
      </Alert>
    );
  }

  const readOnly = !canManageCompany;
  const contact = localConfig.profile?.contactInfo || {};

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {warning && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>{warning}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view company settings but saving changes requires the company management permission.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company profile */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<BusinessIcon />} title="Company Profile" subheader="Basic company information" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={localConfig.profile?.displayName || ''}
                    onChange={(e) => handleSettingChange('profile', 'displayName', e.target.value)}
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Industry</InputLabel>
                    <Select
                      value={localConfig.profile?.industry || ''}
                      onChange={(e) => handleSettingChange('profile', 'industry', e.target.value)}
                      label="Industry"
                    >
                      {['Financial Services', 'Healthcare', 'Technology', 'Manufacturing', 'Retail', 'Government',
                        'Education', 'Energy', 'Transportation', 'Other'].map(v => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Company Size</InputLabel>
                    <Select
                      value={localConfig.profile?.size || ''}
                      onChange={(e) => handleSettingChange('profile', 'size', e.target.value)}
                      label="Company Size"
                    >
                      <MenuItem value="1-10">1-10 employees</MenuItem>
                      <MenuItem value="11-50">11-50 employees</MenuItem>
                      <MenuItem value="51-200">51-200 employees</MenuItem>
                      <MenuItem value="201-1000">201-1000 employees</MenuItem>
                      <MenuItem value="1000+">1000+ employees</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={contact.website || ''}
                    onChange={(e) => handleContactChange('website', e.target.value)}
                    placeholder="https://www.example.com"
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={contact.phone || ''}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    disabled={readOnly}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Address */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Address Information" subheader="Company location details" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Address" value={contact.address || ''} onChange={(e) => handleContactChange('address', e.target.value)} disabled={readOnly} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="City" value={contact.city || ''} onChange={(e) => handleContactChange('city', e.target.value)} disabled={readOnly} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="State/Province" value={contact.state || ''} onChange={(e) => handleContactChange('state', e.target.value)} disabled={readOnly} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Country" value={contact.country || ''} onChange={(e) => handleContactChange('country', e.target.value)} disabled={readOnly} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Postal Code" value={contact.postalCode || ''} onChange={(e) => handleContactChange('postalCode', e.target.value)} disabled={readOnly} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Branding */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<PaletteIcon />} title="Branding" subheader="Customize your company's appearance" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar src={localConfig.branding?.logo} sx={{ width: 64, height: 64 }} variant="rounded">
                      <BusinessIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Company Logo</Typography>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="logo-upload"
                        type="file"
                        onChange={handleLogoUpload}
                        disabled={readOnly}
                      />
                      <Box display="flex" gap={1}>
                        <label htmlFor="logo-upload">
                          <Button variant="outlined" component="span" startIcon={<UploadIcon />} size="small" disabled={readOnly}>
                            Upload Logo
                          </Button>
                        </label>
                        {localConfig.branding?.logo && (
                          <Button
                            size="small"
                            color="inherit"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleSettingChange('branding', 'logo', '')}
                            disabled={readOnly}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        PNG, JPEG, SVG or WebP up to 200 KB; resized to {MAX_LOGO_PX}px.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {[
                  ['primaryColor', 'Primary Color', '#1976d2'],
                  ['secondaryColor', 'Secondary Color', '#dc004e'],
                  ['accentColor', 'Accent Color', '#ff9800']
                ].map(([field, label, fallback]) => (
                  <Grid item xs={12} sm={4} key={field}>
                    <TextField
                      fullWidth
                      label={label}
                      type="color"
                      value={localConfig.branding?.[field] || fallback}
                      onChange={(e) => handleSettingChange('branding', field, e.target.value)}
                      disabled={readOnly}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Default Theme</InputLabel>
                    <Select
                      value={localConfig.branding?.theme || 'light'}
                      onChange={(e) => handleSettingChange('branding', 'theme', e.target.value)}
                      label="Default Theme"
                    >
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="dark">Dark</MenuItem>
                      <MenuItem value="auto">Auto (System)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Localization */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<LanguageIcon />} title="Localization" subheader="Regional and language settings" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={localConfig.profile?.timezone || 'UTC'}
                      onChange={(e) => handleSettingChange('profile', 'timezone', e.target.value)}
                      label="Timezone"
                    >
                      <MenuItem value="UTC">UTC</MenuItem>
                      <MenuItem value="America/New_York">Eastern Time</MenuItem>
                      <MenuItem value="America/Chicago">Central Time</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                      <MenuItem value="Europe/London">London</MenuItem>
                      <MenuItem value="Europe/Paris">Paris</MenuItem>
                      <MenuItem value="Asia/Tokyo">Tokyo</MenuItem>
                      <MenuItem value="Asia/Shanghai">Shanghai</MenuItem>
                      <MenuItem value="Australia/Sydney">Sydney</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Locale</InputLabel>
                    <Select
                      value={localConfig.profile?.locale || 'en-US'}
                      onChange={(e) => handleSettingChange('profile', 'locale', e.target.value)}
                      label="Locale"
                    >
                      <MenuItem value="en-US">English (US)</MenuItem>
                      <MenuItem value="en-GB">English (UK)</MenuItem>
                      <MenuItem value="fr-FR">French</MenuItem>
                      <MenuItem value="de-DE">German</MenuItem>
                      <MenuItem value="es-ES">Spanish</MenuItem>
                      <MenuItem value="ja-JP">Japanese</MenuItem>
                      <MenuItem value="zh-CN">Chinese (Simplified)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Date Format</InputLabel>
                    <Select
                      value={localConfig.ui?.dateFormat || 'MM/DD/YYYY'}
                      onChange={(e) => handleSettingChange('ui', 'dateFormat', e.target.value)}
                      label="Date Format"
                    >
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Time Format</InputLabel>
                    <Select
                      value={localConfig.ui?.timeFormat || '12h'}
                      onChange={(e) => handleSettingChange('ui', 'timeFormat', e.target.value)}
                      label="Time Format"
                    >
                      <MenuItem value="12h">12 Hour</MenuItem>
                      <MenuItem value="24h">24 Hour</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={readOnly ? 'Requires company management permission' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || readOnly}
              size="large"
            >
              {saving ? 'Saving...' : 'Save Company Settings'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default CompanySettings;
